import React from 'react';
import { Mail, TrendingUp, Crown, Plus } from 'lucide-react';

const ServicesPackages = () => {
  const packages = [
    {
      icon: <Mail className="h-8 w-8 text-burnt-orange" />,
      title: "Starter",
      subtitle: "Light Support",
      bestFor: "busy professionals who need inbox + calendar relief.",
      includes: [
        "inbox sorting + priority flags",
        "meeting scheduling + calendar clean-up",
        "draft replies for common messages",
        "follow-up reminders",
        "simple weekly check-in"
      ],
      timeCoverage: "[X hours/week]",
      sla: "[e.g., within 24 hours on business days]",
      ctaText: "Start Starter",
      microcopy: "Great entry point if you're overwhelmed and need quick relief."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-burnt-orange" />,
      title: "Growth",
      subtitle: "Weekly Ops Support",
      bestFor: "leaders running active projects and juggling people.",
      includes: [
        "everything in Starter, plus:",
        "task tracking + follow-through",
        "vendor/client coordination",
        "weekly operations planning",
        "document formatting + research support",
        "simple systems (templates, trackers, checklists)"
      ],
      timeCoverage: "[X–Y hours/week]",
      sla: "[e.g., same-day on business days]",
      ctaText: "Choose Growth",
      microcopy: "Ideal if you want consistency, not just help.",
      featured: true
    },
    {
      icon: <Crown className="h-8 w-8 text-burnt-orange" />,
      title: "Executive",
      subtitle: "High-Touch",
      bestFor: "founders/execs with heavy volume and multiple moving parts.",
      includes: [
        "everything in Growth, plus:",
        "high-priority fast-response support",
        "deeper calendar defense + meeting hygiene",
        "proactive planning + daily triage",
        "multi-project coordination",
        "tighter confidentiality + access workflows"
      ],
      timeCoverage: "[Y–Z hours/week]",
      sla: "[e.g., 2–4 hours during business days]",
      ctaText: "Apply for Executive",
      microcopy: "Limited slots. Built for leaders who move fast."
    }
  ];

  const addOns = [
    "Extra hours for special weeks",
    "Travel / event scheduling support",
    "Hiring/admin assistance (screening, scheduling, coordination)",
    "Process documentation (SOPs, playbooks, internal checklists)"
  ];

  return (
    <section id="services-packages" className="section-padding bg-white">
      <div className="container-center">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-16 animate-slide-up">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-olive-brown">
            Packages (Full Detail)
          </h2>
          <p className="text-olive-brown/70">
            Note: Replace "₦ / $ / £" and exact pricing later. I'll leave clean placeholders.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {packages.map((pkg, index) => (
            <div 
              key={index}
              className={`relative bg-white rounded-3xl p-8 card-shadow transition-all duration-500 animate-slide-up ${
                pkg.featured ? 'ring-2 ring-burnt-orange transform scale-105' : 'hover:shadow-xl'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {pkg.featured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-burnt-orange text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="space-y-6">
                {/* Icon and Title */}
                <div className="space-y-4">
                  <div className="p-4 bg-burnt-orange/20 rounded-2xl w-fit">
                    {pkg.icon}
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-olive-brown mb-1">
                      Package {index + 1} — {pkg.title}
                    </h3>
                    <h4 className="text-lg font-semibold text-burnt-orange mb-4">
                      ({pkg.subtitle})
                    </h4>
                  </div>
                </div>

                {/* Best For */}
                <div>
                  <p className="text-olive-brown/80">
                    <strong>Best for:</strong> {pkg.bestFor}
                  </p>
                </div>

                {/* Includes */}
                <div className="space-y-3">
                  <h5 className="font-semibold text-olive-brown">Includes:</h5>
                  <div className="space-y-2">
                    {pkg.includes.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-burnt-orange rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-olive-brown/80 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Time Coverage & SLA */}
                <div className="space-y-3 pt-4 border-t border-olive-brown/20">
                  <p className="text-sm text-olive-brown/70">
                    <strong>Typical weekly time coverage:</strong> {pkg.timeCoverage}
                  </p>
                  <p className="text-sm text-olive-brown/70">
                    <strong>SLA / response window:</strong> {pkg.sla}
                  </p>
                </div>

                {/* CTA Button */}
                <div className="pt-6">
                  <a
                    href="#contact"
                    className={`w-full inline-block text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                      pkg.featured 
                        ? 'bg-burnt-orange text-white hover:bg-burnt-orange/90' 
                        : 'bg-olive-brown text-white hover:bg-olive-brown/90'
                    }`}
                    aria-label={`${pkg.ctaText} - ${pkg.microcopy}`}
                  >
                    {pkg.ctaText}
                  </a>
                </div>

                {/* Microcopy */}
                <div className="text-center">
                  <p className="text-sm text-olive-brown/60 italic">
                    {pkg.microcopy}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 rounded-3xl p-8">
            <div className="text-center space-y-8">
              <div className="flex items-center justify-center space-x-3">
                <Plus className="h-8 w-8 text-burnt-orange" />
                <h3 className="font-display text-3xl font-bold text-olive-brown">
                  Add-ons when needed (no pressure).
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {addOns.map((addOn, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-burnt-orange rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-olive-brown/80">{addOn}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-burnt-orange font-medium italic">
                Add-ons are optional and scoped clearly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPackages;